import { useTranslation } from "react-i18next";

/**
 * Resolves a home-dashboard copy key to its translated label.
 *
 * The home dashboard is data-driven: greeting keys (greeting.ts), hero action headlines /
 * labels / sublabels (homeConfig.tsx) and panel titles / empty states (panels/*) are all
 * resolved at runtime from a key. A plain `t(key)` is invisible to i18next-parser, so those
 * keys get pruned from the locale files on the next `npm run langen` — silently dropping
 * their Norwegian translations. Listing them here as literal `t("…")` calls (the useXxxLabel
 * pattern, cf. useNavLabel) keeps them extracted and translatable.
 */
export const useHomeLabel = () => {
  const { t } = useTranslation();
  const labels: Record<string, string> = {
    // Greeting (greeting.ts)
    "Good morning": t("Good morning"),
    "Good afternoon": t("Good afternoon"),
    "Good evening": t("Good evening"),
    // Headlines (homeConfig.tsx)
    "What will you work on today?": t("What will you work on today?"),
    "What are you looking for?": t("What are you looking for?"),
    "Find your next opportunity": t("Find your next opportunity"),
    // Hero action labels (homeConfig.tsx)
    "Start procurement": t("Start procurement"),
    "Assess need": t("Assess need"),
    "Active agreements": t("Active agreements"),
    "Report need": t("Report need"),
    "My needs": t("My needs"),
    "Find procurements": t("Find procurements"),
    "My bids": t("My bids"),
    "My requests": t("My requests"),
    // Hero action sublabels (homeConfig.tsx)
    "Create and announce": t("Create and announce"),
    "Review reported needs": t("Review reported needs"),
    "Your live contracts": t("Your live contracts"),
    "Register a new need": t("Register a new need"),
    "Your registered needs": t("Your registered needs"),
    "Browse published competitions": t("Browse published competitions"),
    "Your offers in progress": t("Your offers in progress"),
    "Your participation requests": t("Your participation requests"),
    // Panel titles & empty states (panels/*)
    "My active procurements": t("My active procurements"),
    "My latest in progress": t("My latest in progress"),
    "No active procurements yet": t("No active procurements yet"),
    "No active contracts": t("No active contracts"),
    "Nothing in progress": t("Nothing in progress"),
    "No needs yet": t("No needs yet"),
    "Open procurements": t("Open procurements"),
    "No offers yet": t("No offers yet"),
    "No open procurements right now": t("No open procurements right now"),
  };
  return (key: string): string => labels[key] ?? key;
};
